import { useTranslations } from 'next-intl';
import { ReactNode } from 'react';

import { MfaVerifyChannel } from '@flipster/apis/v1/mfa/types';
import { Explain } from '@flipster/react/design-utils/explain';
import { FormattedInput } from '@flipster/react/design-utils/formatted-input';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';

interface Props {
  channel: MfaVerifyChannel;
  value?: string;
  errorMessage?: string;
  onChange: (code: string) => void;
  length?: number;
  placeholder?: string;
  disabled?: boolean;
  action?: ReactNode;
  label?: string;
  description?: string;
}

export const OtpInput = ({
  channel,
  value,
  errorMessage,
  onChange,
  length = 6,
  placeholder,
  disabled,
  action,
  label,
  description,
}: Props) => {
  const t = useTranslations();

  return (
    <div className={stack({ gap: 6 })}>
      <label
        htmlFor={channel}
        className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-4', mb: 10 })}
      >
        {label}
      </label>
      <div
        className={hstack({
          border: '1px solid',
          borderColor: 'border.default.level-3',
          h: 50,
          px: 12,
          rounded: 4,
          alignContent: 'center',
        })}
      >
        <FormattedInput
          id={channel}
          value={value}
          className={css({
            w: '100%',
            h: '100%',
            color: 'content.default.level-4',
            _disabled: { color: 'content.default.level-1' },
            _placeholder: { color: 'content.default.level-2' },
          })}
          maxLength={length}
          formatter={(val) => val.replace(/\D/g, '')}
          onChange={onChange}
          inputMode='numeric'
          autoComplete='one-time-code'
          autoCorrect='off'
          placeholder={placeholder ?? t('Mfa_verify.r250710.Enter_code_placeholder')}
          disabled={disabled}
        />
        {action}
      </div>
      <div>
        {errorMessage && <Explain.Error css={{ textStyle: 'Text/S/Monospace/Regular' }}>{errorMessage}</Explain.Error>}
        {!errorMessage && description && (
          <Explain css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' }}>
            {description}
          </Explain>
        )}
      </div>
    </div>
  );
};
